import { useState, useEffect } from 'react';
import Sidebar from '@/components/Sidebar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Plus, ClipboardList, Trash2, Edit2, Calendar, User, Bell, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RESPONSIBLE_OPTIONS = [
  { value: 'dj', label: 'DJ / Owner' },
  { value: 'employee', label: 'Employee' },
  { value: 'client', label: 'Client' }
];

export default function TaskSettingsPage({ user, onLogout, isEmployee, permissions, employeeInfo }) {
  const [templates, setTemplates] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    days_offset: 0,
    responsible: 'dj',
    event_types: [],
    notify_client: false,
    is_active: true
  });

  useEffect(() => {
    fetchTemplates();
    fetchEventTypes();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/task-templates`);
      setTemplates(response.data || []);
    } catch (error) {
      console.error('Failed to fetch task templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEventTypes = async () => {
    try {
      const response = await axios.get(`${API}/event-types`);
      setEventTypes(response.data || []);
    } catch (error) {
      console.error('Failed to fetch event types:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      days_offset: 0,
      responsible: 'dj',
      event_types: [],
      notify_client: false,
      is_active: true
    });
    setEditingTemplate(null);
  };

  const openDialog = (template = null) => {
    if (template) {
      setEditingTemplate(template);
      setFormData({
        name: template.name,
        description: template.description || '',
        days_offset: template.days_offset,
        responsible: template.responsible,
        event_types: template.event_types || [],
        notify_client: template.notify_client || false,
        is_active: template.is_active !== false
      });
    } else {
      resetForm();
    }
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Task name is required');
      return;
    }

    setSaving(true);
    try {
      if (editingTemplate) {
        await axios.put(`${API}/task-templates/${editingTemplate.id}`, formData);
        toast.success('Task template updated');
      } else {
        await axios.post(`${API}/task-templates`, formData);
        toast.success('Task template created');
      }
      fetchTemplates();
      setShowDialog(false);
      resetForm();
    } catch (error) {
      toast.error('Failed to save task template');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this task template?')) return;
    
    try {
      await axios.delete(`${API}/task-templates/${templateId}`);
      toast.success('Task template deleted');
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to delete task template');
    }
  };

  const toggleEventType = (eventTypeName) => {
    setFormData(prev => ({
      ...prev,
      event_types: prev.event_types.includes(eventTypeName)
        ? prev.event_types.filter(e => e !== eventTypeName)
        : [...prev.event_types, eventTypeName]
    }));
  };

  const formatDaysOffset = (days) => {
    if (days === 0) return 'On event day';
    if (days < 0) return `${Math.abs(days)} day${Math.abs(days) > 1 ? 's' : ''} before event`;
    return `${days} day${days > 1 ? 's' : ''} after event`;
  };

  const getResponsibleLabel = (value) => {
    return RESPONSIBLE_OPTIONS.find(o => o.value === value)?.label || value;
  };

  return (
    <div className="flex min-h-screen bg-obsidian" data-testid="task-settings-page">
      <Sidebar user={user} onLogout={onLogout} isEmployee={isEmployee} permissions={permissions} employeeInfo={employeeInfo} />
      
      <div className="flex-1 overflow-auto">
        <div className="p-8">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="font-heading text-4xl font-bold mb-2" data-testid="task-settings-heading">
                Task Templates
              </h1>
              <p className="text-muted-foreground">
                Define tasks that are automatically assigned to bookings based on event type and date
              </p>
            </div>
            <Dialog open={showDialog} onOpenChange={setShowDialog}>
              <DialogTrigger asChild>
                <Button 
                  className="bg-neon-blue hover:bg-neon-blue/90"
                  onClick={() => openDialog()}
                  data-testid="add-task-template-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Task Template
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-card border border-border max-w-lg" data-testid="task-template-dialog">
                <DialogHeader>
                  <DialogTitle className="font-heading text-xl flex items-center gap-2">
                    <ClipboardList className="w-5 h-5 text-neon-purple" />
                    {editingTemplate ? 'Edit Task Template' : 'New Task Template'}
                  </DialogTitle>
                </DialogHeader>
                
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label>Task Name *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="e.g., Final Payment Due"
                      className="bg-muted/20 border-glass-border"
                      data-testid="task-name-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Optional description or instructions"
                      className="bg-muted/20 border-glass-border min-h-[80px]"
                      data-testid="task-description-input"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Days Before/After Event</Label>
                      <div className="flex gap-2 items-center">
                        <Input
                          type="number"
                          value={formData.days_offset}
                          onChange={(e) => {
                            const val = e.target.value;
                            // Allow empty, minus sign, or valid number
                            if (val === '' || val === '-') {
                              setFormData({ ...formData, days_offset: val });
                            } else {
                              const parsed = parseInt(val, 10);
                              if (!isNaN(parsed)) {
                                setFormData({ ...formData, days_offset: parsed });
                              }
                            }
                          }}
                          onBlur={(e) => {
                            // On blur, convert empty or minus-only to 0
                            const val = formData.days_offset;
                            if (val === '' || val === '-') {
                              setFormData({ ...formData, days_offset: 0 });
                            }
                          }}
                          className="bg-muted/20 border-glass-border w-24"
                          data-testid="days-offset-input"
                        />
                        <span className="text-sm text-muted-foreground">
                          {formatDaysOffset(typeof formData.days_offset === 'number' ? formData.days_offset : 0)}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Negative = before event, Positive = after event
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label>Responsible</Label>
                      <Select 
                        value={formData.responsible} 
                        onValueChange={(v) => setFormData({ ...formData, responsible: v })}
                      >
                        <SelectTrigger className="bg-muted/20 border-glass-border" data-testid="responsible-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-card border-glass-border">
                          {RESPONSIBLE_OPTIONS.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Event Types</Label>
                    <p className="text-xs text-muted-foreground mb-2">
                      Select which event types this task applies to. Leave empty to apply to all.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {eventTypes.filter(et => et.active !== false).map((eventType) => (
                        <Button
                          key={eventType.id}
                          type="button"
                          variant={formData.event_types.includes(eventType.name) ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => toggleEventType(eventType.name)}
                          className={formData.event_types.includes(eventType.name) 
                            ? 'bg-neon-purple hover:bg-neon-purple/90' 
                            : 'border-glass-border'}
                          data-testid={`event-type-${eventType.name}`}
                        >
                          {eventType.name}
                        </Button>
                      ))}
                      {eventTypes.length === 0 && (
                        <p className="text-sm text-muted-foreground">No event types configured</p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-muted/20 rounded-lg">
                    <div>
                      <Label className="text-base">Notify Client</Label>
                      <p className="text-sm text-muted-foreground">
                        Send notification to client when this task is due
                      </p>
                    </div>
                    <Switch
                      checked={formData.notify_client}
                      onCheckedChange={(v) => setFormData({ ...formData, notify_client: v })}
                      data-testid="notify-client-switch"
                    />
                  </div>

                  <div className="flex items-center justify-between p-4 bg-muted/20 rounded-lg">
                    <div>
                      <Label className="text-base">Active</Label>
                      <p className="text-sm text-muted-foreground">
                        Inactive templates won't be assigned to new bookings
                      </p>
                    </div>
                    <Switch
                      checked={formData.is_active}
                      onCheckedChange={(v) => setFormData({ ...formData, is_active: v })}
                      data-testid="is-active-switch"
                    />
                  </div>
                </div>

                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowDialog(false)} className="border-glass-border">
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleSave} 
                    disabled={saving}
                    className="bg-neon-blue hover:bg-neon-blue/90"
                    data-testid="save-task-template-btn"
                  >
                    {saving ? 'Saving...' : editingTemplate ? 'Update Template' : 'Create Template'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          {loading ? (
            <div className="text-center py-12">Loading task templates...</div>
          ) : templates.length === 0 ? (
            <Card className="glass-card border-glass-border">
              <CardContent className="py-16 text-center">
                <ClipboardList className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
                <h3 className="text-xl font-semibold mb-2">No Task Templates</h3>
                <p className="text-muted-foreground mb-4">
                  Create task templates to automatically assign tasks to bookings based on event type and date
                </p>
                <Button 
                  className="bg-neon-blue hover:bg-neon-blue/90"
                  onClick={() => openDialog()}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Your First Template
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {templates.map((template) => (
                <Card 
                  key={template.id} 
                  className={`glass-card border-glass-border ${!template.is_active ? 'opacity-60' : ''}`}
                  data-testid={`task-template-${template.id}`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-semibold text-lg">{template.name}</h3>
                          {!template.is_active && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                              Inactive
                            </span>
                          )}
                          {template.notify_client && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-neon-blue/20 text-neon-blue flex items-center gap-1">
                              <Bell className="w-3 h-3" />
                              Notifies Client
                            </span>
                          )}
                        </div>
                        
                        {template.description && (
                          <p className="text-sm text-muted-foreground mb-3">{template.description}</p>
                        )}
                        
                        <div className="flex flex-wrap items-center gap-4 text-sm">
                          <span className="flex items-center gap-1.5 text-muted-foreground">
                            <Calendar className="w-4 h-4" />
                            {formatDaysOffset(template.days_offset)}
                          </span>
                          <span className="flex items-center gap-1.5 text-muted-foreground">
                            <User className="w-4 h-4" />
                            {getResponsibleLabel(template.responsible)}
                          </span>
                          {template.event_types && template.event_types.length > 0 && (
                            <span className="flex items-center gap-1.5">
                              <span className="text-muted-foreground">Events:</span>
                              {template.event_types.map((et, i) => (
                                <span key={et} className="px-2 py-0.5 rounded-full bg-neon-purple/20 text-neon-purple text-xs">
                                  {et}
                                </span>
                              ))}
                            </span>
                          )}
                          {(!template.event_types || template.event_types.length === 0) && (
                            <span className="text-muted-foreground">
                              Applies to all event types
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openDialog(template)}
                          className="text-muted-foreground hover:text-foreground"
                          data-testid={`edit-template-${template.id}`}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(template.id)}
                          className="text-muted-foreground hover:text-red-500"
                          data-testid={`delete-template-${template.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Info Card */}
          <Card className="glass-card border-glass-border mt-8">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-500" />
                How Task Templates Work
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground space-y-2">
              <p>• Task templates define recurring tasks that should be completed for each booking</p>
              <p>• Tasks are automatically generated when you click "Generate Tasks" on a booking</p>
              <p>• The due date is calculated relative to the event date (e.g., -7 = 7 days before)</p>
              <p>• You can assign responsibility to DJ/Owner, Employee, or Client</p>
              <p>• Filter by event type to have different task lists for different events</p>
              <p>• Enable "Notify Client" to send reminders for client-responsible tasks</p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
